import {
  ArrowDownIcon,
  ArrowUpIcon,
  Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { PaginationMeta } from '@sr-sdks/api-sdk-axios';
import { Icon, IconKey, Button as SRButton } from '@sr/ui-library';
import clsx from 'clsx';
import { ReactNode, useEffect, useState } from 'react';
import { Button, ButtonGroup } from 'react-daisyui';
import { useSearchParams } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';
import EmptyValueErrorLayout from '../pages/Workspace/EmptyValueErrorLayout';
import './AppTable.css';
import AppTablePageCount from './AppTablePageCount';
import { AppLoader } from './Loader';

export type EmptyTableProps = {
  description: ReactNode;
  icon?: IconKey | PhosphorIcon;
  title: string;
};

export interface IAppTableV2 {
  children: ReactNode;
  className?: string;
  customEmptyTable?: EmptyTableProps;
  customPageCountElement?: ReactNode;
  emptyTableMessage?: string;
  filterElement?: ReactNode;
  filterElementV2?: ReactNode;
  footerFilterElement?: ReactNode;
  hasBottomNavigation?: boolean;
  hasFooterCount?: boolean;
  headerRowElement: ReactNode;
  id?: string;
  /**
   * True while a subsequent page/sort/filter fetch is in flight and rows
   * from the previous page are still on screen (e.g. TanStack's
   * `isPlaceholderData` with `placeholderData: keepPreviousData`, or
   * `isFetching && !isLoading`). Keeps rows rendered — no flash of empty
   * content — but layers the same loading overlay `isLoading` shows over
   * them and disables pagination.
   */
  isFetchingPage?: boolean;
  /**
   * True first load only — drives the empty-state gate and the same overlay
   * `isFetchingPage` triggers below. Unset (default false) preserves every
   * existing caller's behaviour.
   */
  isLoading?: boolean;
  onPageRequest?: (page: number, sortBy?: string) => void;
  /**
   * What URL changes re-invoke `onPageRequest`.
   * - `allSearchParams` (default): legacy tables that fetch inside the handler
   *   and rely on this effect for search/filter refetch (Payouts, Manage
   *   Workspaces, Workspace Invitations).
   * - `pageAndSort`: URL-owned MSR3 tables whose list query already tracks
   *   search; notifying on every search keystroke would rewrite `sortBy`.
   */
  pageRequestDeps?: 'allSearchParams' | 'pageAndSort';
  paginationKey?: string;
  paginationMeta?: PaginationMeta | null;
  reportTrail?: ReactNode;
  /**
   * URL query param for column sort. Defaults to the shared `sortBy` key.
   * Namespaced tables (Royalties ANZ) pass a dedicated key so other pages
   * cannot poison sort on first paint.
   */
  sortByKey?: string;
}

type PaginationType = {
  firstRowNumber: number;
  lastRowNumber: number;
};

enum Go {
  Next = +1,
  Prev = -1,
}

const DEFAULT_SORT_BY_KEY = 'sortBy';

const AppTableV2 = ({
  children,
  className,
  customEmptyTable,
  customPageCountElement,
  emptyTableMessage,
  filterElement,
  filterElementV2,
  footerFilterElement,
  hasBottomNavigation,
  hasFooterCount,
  headerRowElement,
  id,
  isFetchingPage,
  isLoading,
  onPageRequest,
  pageRequestDeps = 'allSearchParams',
  paginationKey = 'page',
  paginationMeta,
  reportTrail,
  sortByKey = DEFAULT_SORT_BY_KEY,
}: IAppTableV2) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [paginationValues, setPaginationValues] = useState<PaginationType>({
    firstRowNumber: 0,
    lastRowNumber: 0,
  });

  const currentPage = Number(searchParams.get(paginationKey)) || 1;
  const sortBy = searchParams.get(sortByKey) ?? undefined;

  // Drives the overlay + disabled controls for BOTH a first load (`isLoading`)
  // and an in-between page/sort fetch (`isFetchingPage`) — the two states
  // render identically, just with different rows underneath.
  const disableControls = isLoading || isFetchingPage;

  // Narrow notify key for URL-owned tables; legacy tables watch the full query.
  const pageRequestNotifyKey =
    pageRequestDeps === 'pageAndSort'
      ? `${currentPage}|${sortBy ?? ''}`
      : searchParams.toString();

  const handlePaginationClick = (direction: Go) => {
    if (!paginationMeta || !onPageRequest) return;

    const pageNum = currentPage + direction;

    if (pageNum < 1 || pageNum > paginationMeta.totalPages) return;

    setSearchParams(
      (prev) => {
        prev.set(paginationKey, pageNum.toString());

        return prev;
      },
      { replace: true },
    );
  };

  useEffect(() => {
    if (paginationMeta == null) return;

    const { currentPage, itemCount, itemsPerPage, totalItems, totalPages } =
      paginationMeta;

    const firstRowNumber = itemsPerPage * currentPage - itemsPerPage;

    const lastRowNumber =
      totalPages === currentPage ? totalItems : currentPage * itemCount;

    setPaginationValues({
      firstRowNumber: totalItems > 0 ? firstRowNumber + 1 : firstRowNumber,
      lastRowNumber: lastRowNumber,
    });
  }, [
    paginationMeta,
    paginationMeta?.currentPage,
    paginationMeta?.itemCount,
    paginationMeta?.itemsPerPage,
    paginationMeta?.totalItems,
    paginationMeta?.totalPages,
  ]);

  useEffect(() => {
    if (!onPageRequest) return;

    onPageRequest(currentPage, sortBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callers often pass an inline onPageRequest
  }, [pageRequestNotifyKey]);

  return (
    <div
      className={twMerge(
        'flex w-full flex-col items-center justify-center',
        hasFooterCount && !hasBottomNavigation && !footerFilterElement
          ? 'mb-[40px]'
          : '',
        filterElement || filterElementV2 ? 'gap-[30px]' : '',
      )}
    >
      <div className="flex flex-col items-center justify-center gap-5 self-stretch">
        <div className="flex w-full items-center justify-end gap-5 self-stretch">
          {filterElement && (
            <div className="flex w-full items-center gap-3">
              {filterElement}
            </div>
          )}
          {reportTrail && (
            <div className="relative -left-20 -mt-5 w-full">
              <div className="absolute right-0">{reportTrail}</div>
            </div>
          )}
          {/*
           * `filterElement` is a `w-full` flex sibling, so it claims the row and
           * squeezes this cell toward its min-content width — left to wrap, a
           * range like "1-25 of 61" breaks over three lines (APP-2344). Nowrap
           * fixes that on its own: it makes min-content the whole string, and a
           * flex item will not shrink below its automatic minimum size. It sits
           * here rather than on the default branch below because `white-space`
           * inherits, so `customPageCountElement` callers are covered too.
           *
           * Deliberately not `shrink-0`: BeneficiaryAccessTable passes a legend
           * that has to stay shrinkable so its own `flex-wrap` can stack it.
           */}
          <div className="whitespace-nowrap text-sm text-read-only-text">
            {customPageCountElement ?? (
              <div className="float-right">
                <AppTablePageCount
                  isLoading={disableControls}
                  paginationMeta={paginationMeta}
                  paginationValues={paginationValues}
                  onPaginationClick={handlePaginationClick}
                />
              </div>
            )}
          </div>
        </div>
      </div>
      {filterElementV2 && filterElementV2}
      {!isLoading && paginationMeta?.totalItems === 0 ? (
        customEmptyTable ? (
          <div className="flex w-full flex-col items-center justify-center gap-8 p-10 text-center">
            <Icon
              className="!size-16 text-read-only-bg"
              data-testid="custom-empty-table-icon"
              icon={customEmptyTable?.icon || 'folderOpen'}
            />
            <h3 className="text-sr-black">{customEmptyTable.title}</h3>
            <span className="items-center text-base font-normal text-sr-black">
              {customEmptyTable.description}
            </span>
          </div>
        ) : (
          <div className="mt-2 w-full text-center">
            {emptyTableMessage ?? (
              <EmptyValueErrorLayout contentType="no-result" />
            )}
          </div>
        )
      ) : (
        <>
          <div className="relative w-full overflow-x-auto overflow-y-clip">
            <table
              id={id}
              className={twMerge(
                'app-table table-compact table w-full min-w-max table-auto text-left',
                className,
              )}
            >
              <thead className="border-b-2">{headerRowElement}</thead>
              <tbody>
                {paginationMeta === null ||
                (paginationMeta && paginationMeta?.totalItems > 0)
                  ? children
                  : null}
              </tbody>
            </table>
            {disableControls && (
              <div className="absolute inset-0 z-0">
                <AppLoader.Overlay />
              </div>
            )}
          </div>
          <div className="relative flex w-full gap-3">
            {hasFooterCount && (
              <div className="absolute right-0 text-sm text-read-only-text">
                <div className="float-right items-center justify-end whitespace-nowrap">
                  <AppTablePageCount
                    isLoading={disableControls}
                    paginationMeta={paginationMeta}
                    paginationValues={paginationValues}
                    onPaginationClick={handlePaginationClick}
                  />
                </div>
              </div>
            )}
            {footerFilterElement
              ? footerFilterElement
              : paginationMeta !== null &&
                hasBottomNavigation && (
                  <ButtonGroup className="app-pagination w-full place-content-center">
                    <Button
                      color="primary"
                      disabled={
                        disableControls ||
                        paginationMeta?.totalItems === 0 ||
                        paginationMeta?.currentPage === 1
                      }
                      startIcon={<Icon icon="chevronLeft" size="sm" />}
                      variant="outline"
                      onClick={() => handlePaginationClick(Go.Prev)}
                    >
                      <span className="sr-only">Previous</span>
                    </Button>
                    <div
                      className={twMerge(
                        'content-center px-4 py-2 text-sm text-primary ring-1 ring-inset ring-primary',
                        clsx({
                          '!text-read-only-text opacity-50 !ring-gray-400':
                            disableControls,
                        }),
                      )}
                    >
                      Page {paginationMeta?.currentPage ?? 1}
                    </div>
                    <Button
                      color="primary"
                      disabled={
                        disableControls ||
                        paginationMeta?.totalItems === 0 ||
                        paginationMeta?.currentPage ===
                          paginationMeta?.totalPages
                      }
                      endIcon={<Icon icon="chevronRight" size="xl" />}
                      variant="outline"
                      onClick={() => handlePaginationClick(Go.Next)}
                    >
                      <span className="sr-only">Next</span>
                    </Button>
                  </ButtonGroup>
                )}
          </div>
        </>
      )}
    </div>
  );
};

export interface IAppTableV2HeaderCellProps {
  children: ReactNode;
  className?: string;
  hideLabel?: boolean;
  /** URL query param for this column's sort. Defaults to shared `sortBy`. */
  sortByKey?: string;
  sortByProperty?: string;
  sortOrderByProperty?: string;
}

export const AppTableV2HeaderCell = ({
  children,
  className,
  hideLabel,
  sortByKey = DEFAULT_SORT_BY_KEY,
  sortByProperty,
  sortOrderByProperty = 'asc',
}: IAppTableV2HeaderCellProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const handleClick = () => {
    if (!sortByProperty) return;

    const sortByParts = searchParams.get(sortByKey)?.split(':');
    const property = sortByParts?.[0];
    const direction = sortByParts?.[1];

    if (property === sortByProperty) {
      setSearchParams(
        (current) => {
          current.set(
            sortByKey,
            `${sortByProperty}:${direction === 'asc' ? 'desc' : 'asc'}`,
          );

          return current;
        },
        { replace: true },
      );
    } else {
      setSearchParams(
        (current) => {
          current.set(sortByKey, `${sortByProperty}:${sortOrderByProperty}`);

          return current;
        },
        { replace: true },
      );
    }
  };

  const getIcon = () => {
    const sortBy = searchParams.get(sortByKey);

    let active = false;
    let icon = ArrowDownIcon;

    if (sortByProperty && sortBy?.includes(sortByProperty)) {
      active = true;
      icon = sortBy?.includes(':asc') ? ArrowDownIcon : ArrowUpIcon;
    }

    return (
      <Icon
        className={clsx({
          'opacity-50': !active,
        })}
        icon={icon}
      />
    );
  };

  return (
    <th className={className}>
      {sortByProperty ? (
        <SRButton
          className="min-w-16 p-0"
          variant="transparent"
          onClick={() => handleClick()}
        >
          <span className="font-sans text-base font-semibold leading-normal">
            {children}
          </span>
          {getIcon()}
        </SRButton>
      ) : hideLabel ? (
        <span className="sr-only">{children}</span>
      ) : (
        <span className="font-sans text-base font-semibold leading-normal">
          {children}
        </span>
      )}
    </th>
  );
};

export default AppTableV2;
